'use no memo'

import {
  mailingListRecipientFormSchema,
  useAddMailingListRecipient,
  useDestroyMailingListRecipient,
  useMailingList,
  useUpdateMailingListRecipient,
  type MailingListRecipientFormValues,
} from '@gozoki/api'
import type { MailingListRecipient } from '@gozoki/api-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { DeleteConfirmButton } from '~/components/new/buttons/delete-confirm-button'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTable, AppTableData } from '~/components/new/app-ui/table/app-table'
import { Title } from '~/components/new/app-ui/title'
import { RbacAccessDenied } from '~/components/routes/administration/roles/access-denied'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Switch } from '~/components/ui/switch'
import { useAppTable } from '~/hooks/use-app-table'
import { getAxiosErrorMessage } from '~/lib/utils'
import { useRights } from '~/utils/hooks'

export const Route = createFileRoute('/_authenticated/mailing-lists/$key')({
  component: MailingListDetailPage,
})

const columnHelper = createColumnHelper<MailingListRecipient>()

function MailingListDetailPage() {
  const isSuperAdmin = useRights(['SUPER_ADMIN'])
  const { key } = useParams({ from: '/_authenticated/mailing-lists/$key' })

  const { data: mailingList, isLoading } = useMailingList(key)
  const addRecipient = useAddMailingListRecipient(key)
  const updateRecipient = useUpdateMailingListRecipient(key)
  const destroyRecipient = useDestroyMailingListRecipient(key)

  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MailingListRecipientFormValues>({
    resolver: zodResolver(mailingListRecipientFormSchema),
  })

  if (!isSuperAdmin) {
    return <RbacAccessDenied />
  }

  const handleAddDialogOpenChange = (open: boolean) => {
    setAddDialogOpen(open)
    if (!open) reset()
  }

  const onAddSubmit = (values: MailingListRecipientFormValues) => {
    addRecipient.mutate(values, {
      onSuccess: () => {
        toast.success('Destinataire ajouté.')
        setAddDialogOpen(false)
        reset()
      },
      onError: (error) => {
        toast.error(getAxiosErrorMessage(error))
      },
    })
  }

  const handleToggleActive = (recipient: MailingListRecipient) => {
    updateRecipient.mutate(
      { id: recipient.id, payload: { active: !recipient.active } },
      {
        onError: (error) => {
          toast.error(getAxiosErrorMessage(error))
        },
      },
    )
  }

  const handleDelete = (id: number) => {
    destroyRecipient.mutate(id, {
      onError: (error) => {
        toast.error(getAxiosErrorMessage(error))
      },
    })
  }

  const columns = [
    columnHelper.accessor('email', {
      header: 'Email',
    }),

    columnHelper.accessor('name', {
      header: 'Nom',
      cell: ({ getValue }) => getValue() ?? '—',
    }),

    columnHelper.accessor('active', {
      header: 'Actif',
      size: 80,
      cell: ({ row }) => (
        <Switch
          checked={row.original.active}
          disabled={updateRecipient.isPending}
          onCheckedChange={() => handleToggleActive(row.original)}
        />
      ),
    }),

    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 100,
      cell: ({ row }) => (
        <DeleteConfirmButton
          itemLabel={row.original.email}
          onConfirm={() => handleDelete(row.original.id)}
        />
      ),
    }),
  ]

  const table = useAppTable({
    columns,
    data: mailingList?.recipients ?? [],
  })

  return (
    <>
      <AppHeader>
        <Title>{mailingList?.name ?? '…'}</Title>
      </AppHeader>

      <AppContainer className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">{mailingList?.name ?? '…'}</h2>
            {mailingList?.description && (
              <p className="text-muted-foreground text-sm">{mailingList.description}</p>
            )}
          </div>

          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="size-4" />
            Ajouter un destinataire
          </Button>
        </div>

        <AppTable>
          <AppTableData table={table} />
        </AppTable>

        {isLoading && <p className="text-muted-foreground text-center text-sm">Chargement…</p>}
      </AppContainer>

      <Dialog open={addDialogOpen} onOpenChange={handleAddDialogOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un destinataire</DialogTitle>
          </DialogHeader>

          <form id="add-recipient-form" onSubmit={handleSubmit(onAddSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="recipient-email">Email *</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="exemple@domaine.fr"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="recipient-name">Nom</Label>
              <Input
                id="recipient-name"
                type="text"
                placeholder="Nom du destinataire (optionnel)"
                {...register('name')}
              />
              {errors.name && <p className="text-destructive text-xs">{errors.name.message}</p>}
            </div>
          </form>

          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
            <Button
              type="submit"
              form="add-recipient-form"
              disabled={isSubmitting || addRecipient.isPending}
            >
              {addRecipient.isPending ? 'Ajout…' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
